<template>
    <div
        v-if="!hasAnyBenefit && !loading"
        class="min-h-[calc(100vh-200px)] flex items-center justify-center"
    >
        <EmptyState
            icon="coin"
            title="No benefits yet"
            description="We'll display benefits we can track from your cards when you have some."
            :hasAction="true"
            actionLabel="View My Wallet"
            @action="goToWallet"
        />
    </div>

    <div v-else class="flex flex-col gap-6">
        <InsightsFilters />
        <InsightsStats />

        <section
            class="relative flex flex-col items-start gap-6 p-4 sm:p-6 self-stretch bg-white dark:bg-slate-800 rounded-lg shadow-[0_1px_3px_0_rgba(0,0,0,0.10),_0_1px_2px_0_rgba(0,0,0,0.06)]"
        >
            <header class="flex items-center gap-2">
                <h2
                    class="text-slate-800 dark:text-slate-100 font-inter text-[18px] font-medium leading-7"
                >
                    Benefits usage
                </h2>
                <Tooltip
                    text="Shows used and unused benefits in the selected period."
                    :placement="isMobile ? null : 'right'"
                    wrap
                >
                    <template #trigger>
                        <Icon
                            icon="circle-info"
                            class="w-4 h-4 text-slate-400 cursor-help"
                        />
                    </template>
                </Tooltip>
            </header>
            <div
                class="w-full"
                :class="locked && 'pointer-events-none blur-[6px] select-none'"
            >
                <BenefitsUsageChart :series="benefitsUsageSeries" />
            </div>
            <InsightsLockedOverlay
                v-if="locked"
                title="Unlock benefits usage"
            />
        </section>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <section
                :class="[
                    'relative flex flex-col bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4 sm:px-6 sm:py-5 min-h-[292px]',
                    usageByCardExpanded && 'self-start',
                ]"
                :style="
                    usageByCardExpanded && lockedRedeemedHeight
                        ? { minHeight: lockedRedeemedHeight + 'px' }
                        : null
                "
            >
                <header class="flex items-center gap-2 mb-4">
                    <h2
                        class="text-slate-800 dark:text-slate-100 font-inter text-[18px] font-medium leading-7"
                    >
                        Redeemed by card
                    </h2>
                    <Tooltip
                        text="How much value you've got from each card in the selected period"
                        :placement="isMobile ? null : 'right'"
                        wrap
                    >
                        <template #trigger>
                            <Icon
                                icon="circle-info"
                                class="w-4 h-4 text-slate-400 cursor-help"
                            />
                        </template>
                    </Tooltip>
                </header>
                <div
                    class="flex-1 flex items-center"
                    :class="
                        locked && 'pointer-events-none blur-[6px] select-none'
                    "
                >
                    <RedeemedByCardChart :cards="redeemedByCard" />
                </div>
                <InsightsLockedOverlay
                    v-if="locked"
                    title="Unlock benefits usage"
                />
            </section>

            <section
                ref="usageSectionRef"
                class="relative bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4 sm:px-6 sm:py-5 min-h-[292px]"
            >
                <header class="flex items-center gap-2 mb-8">
                    <h2
                        class="text-slate-800 dark:text-slate-100 font-inter text-[18px] font-medium leading-7"
                    >
                        Usage by card
                    </h2>
                    <Tooltip
                        text="How fully each card's benefits are being used in the selected period"
                        :placement="isMobile ? null : 'right'"
                        wrap
                    >
                        <template #trigger>
                            <Icon
                                icon="circle-info"
                                class="w-4 h-4 text-slate-400 cursor-help"
                            />
                        </template>
                    </Tooltip>
                </header>
                <div
                    :class="
                        locked && 'pointer-events-none blur-[6px] select-none'
                    "
                >
                    <UsageByCardList :cards="usageByCard" />
                </div>
                <InsightsLockedOverlay
                    v-if="locked"
                    title="Unlock benefits usage"
                />
            </section>
        </div>

        <div>
            <div class="w-full flex justify-between items-center pt-2 pb-4">
                <span
                    class="text-slate-800 dark:text-slate-100 font-inter text-sm font-medium leading-5"
                >
                    {{ insightsBenefits.length }}
                    {{ insightsBenefits.length === 1 ? 'benefit' : 'benefits' }}
                </span>
                <InsightsDisplayPopover v-if="!isMobile" />
                <div v-else class="flex items-center gap-2">
                    <button
                        class="btn-icon btn-white !py-1.5 h-10"
                        @click="sortModalIsOpen = true"
                        aria-label="Sort"
                    >
                        <Icon icon="sort" class="w-5 h-5 icons-primary" />
                    </button>
                    <button
                        class="btn-icon btn-white !py-1.5 h-10"
                        @click="displayModalIsOpen = true"
                        aria-label="Display settings"
                    >
                        <Icon icon="gear" class="w-5 h-5 icons-primary" />
                    </button>
                </div>
            </div>
            <component
                :is="isMobile ? InsightsTableMobile : InsightsTable"
                :benefits="insightsBenefits"
            />
        </div>

        <BenefitDetailsSidebar v-if="sidebarOpen" />

        <InsightsDisplayModalMobile
            :is-open="displayModalIsOpen && isMobile"
            @close="displayModalIsOpen = false"
        />

        <InsightsSortModalMobile
            :is-open="sortModalIsOpen && isMobile"
            @close="sortModalIsOpen = false"
        />
    </div>
</template>

<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
import { router } from '@inertiajs/vue3';
import { storeToRefs } from 'pinia';
import { useIsMobile } from '@/composables/useIsMobile';
import { useCardBenefitsStore } from '@/store/card-benefits';
import InsightsFilters from './insights-filters.vue';
import InsightsStats from './insights-stats.vue';
import BenefitsUsageChart from './benefits-usage-chart.vue';
import RedeemedByCardChart from './redeemed-by-card-chart.vue';
import UsageByCardList from './usage-by-card-list.vue';
import InsightsTable from './insights-table.vue';
import InsightsTableMobile from './insights-table-mobile.vue';
import InsightsDisplayPopover from './insights-display-popover.vue';
import InsightsDisplayModalMobile from './insights-display-modal-mobile.vue';
import InsightsSortModalMobile from './insights-sort-modal-mobile.vue';
import InsightsLockedOverlay from './insights-locked-overlay.vue';
import BenefitDetailsSidebar from '@/components/card-benefits/benefit-details-sidebar.vue';
import Tooltip from '@/components/tooltip.vue';
import Icon from '@/components/ui/icon/icon.vue';
import EmptyState from '@/components/ui/empty-state/empty-state.vue';

const store = useCardBenefitsStore();
const {
    benefitsUsageSeries,
    redeemedByCard,
    usageByCard,
    insightsBenefits,
    sidebarOpen,
    benefits,
    loading,
    usageByCardExpanded,
    isSubscribed,
} = storeToRefs(store);

const { isMobile } = useIsMobile();
const displayModalIsOpen = ref(false);
const sortModalIsOpen = ref(false);

const hasAnyBenefit = computed(() => benefits.value.length > 0);
// Free users see the Insights scaffolding but historical aggregates,
// charts and widgets are gated behind 10xTravel+.
const locked = computed(() => !isSubscribed.value);

// While the "Usage by card" widget is collapsed, track its rendered height so
// we can lock the "Redeemed by card" sibling to that same value once Usage
// expands. Without this lock, the grid row collapses to Redeemed's natural
// (much smaller) content height as soon as `self-start` kicks in.
const usageSectionRef = ref(null);
const lockedRedeemedHeight = ref(0);
let usageResizeObserver = null;

onMounted(() => {
    if (typeof ResizeObserver === 'undefined' || !usageSectionRef.value) {
        return;
    }
    usageResizeObserver = new ResizeObserver(() => {
        if (!usageByCardExpanded.value && usageSectionRef.value) {
            lockedRedeemedHeight.value = usageSectionRef.value.offsetHeight;
        }
    });
    usageResizeObserver.observe(usageSectionRef.value);
});

onBeforeUnmount(() => {
    if (usageResizeObserver) {
        usageResizeObserver.disconnect();
        usageResizeObserver = null;
    }
});

const goToWallet = () => {
    router.visit('/wallet');
};
</script>
